import { Link, useLocation } from "@tanstack/react-router";
import { ChevronLeft, PanelLeftOpen, Plus } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Breadcrumb } from "@/common/types/types";
import { BackgroundVisitUploadPanel } from "@/components/ui/BackgroundVisitUploadPanel";
import { OpenBackgroundVisitUploadButton } from "@/components/ui/BackgroundVisitUploadPanel/OpenBackgroundVisitUploadButton";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { PendingRecordingsButton } from "@/components/ui/offline-recording/PendingRecordingsButton";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { V3IconButton } from "@/components/ui/v3/button";
import { cn } from "@/lib/utils";
import { useVisitList } from "@/lib/visit-queries";
import { useWorkspacePanelStore } from "@/store/workspacePanel.store";
import type { PageHeaderBar } from "../main/navbar";
import { AccountMenu } from "./AccountMenu";
import { useV3Shell, V3ShellProvider } from "./shell-state";
import { useNewVisit, useNewVisitShortcut, VisitBrowser } from "./VisitBrowser";
import { WORKSPACE_COLUMN_ID } from "./WorkspacePanel";

// Which top bar a page gets: the shell's ("default"), only from 1024px
// ("desktop", e.g. the phone visits screen has its own header), or none
// (the visit workspace draws its own).
export type V3TopBarMode = "default" | "desktop" | "none";

interface Props {
  children: React.ReactNode;
  title: string;
  breadcrumbs?: Breadcrumb[];
  pageHeader?: PageHeaderBar;
  isLoading?: boolean;
  topBar?: V3TopBarMode;
}

// The hidden sidebar as a slim rail (desktop only): show the sidebar, New
// visit, and the account avatar at the bottom.
const SidebarRail = ({ onExpand }: { onExpand: () => void }): JSX.Element => {
  const { t } = useTranslation();
  const startNewVisit = useNewVisit();
  useNewVisitShortcut(startNewVisit, true);
  return (
    <nav
      aria-label={t("v3.shell.navigation")}
      className="hidden w-[60px] shrink-0 flex-col items-center gap-1 py-2 lg:flex"
      data-testid="v3-sidebar-rail"
    >
      <V3IconButton
        data-testid="sidebar-expand"
        label={t("v3.shell.showSidebar")}
        onClick={onExpand}
      >
        <PanelLeftOpen />
      </V3IconButton>
      <V3IconButton
        data-testid="rail-new-visit"
        label={t("v3.shell.newVisit")}
        onClick={startNewVisit}
      >
        <Plus />
      </V3IconButton>
      <div className="mt-auto">
        <AccountMenu variant="rail" />
      </div>
    </nav>
  );
};

// Phones and tablets: back to the visits list (push navigation).
export const BackToVisits = ({
  className,
}: {
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <V3IconButton
      asChild
      className={cn("lg:hidden", className)}
      label={t("v3.shell.backToVisits")}
    >
      <Link data-testid="back-to-visits" to="/visits">
        <ChevronLeft className="size-5!" />
      </Link>
    </V3IconButton>
  );
};

const TopBar = ({
  title,
  breadcrumbs,
  pageHeader,
  mode,
}: {
  title: string;
  breadcrumbs?: Breadcrumb[];
  pageHeader?: PageHeaderBar;
  mode: V3TopBarMode;
}): JSX.Element => {
  const { t } = useTranslation();
  const [uploadsOpen, setUploadsOpen] = useState(false);
  const location = useLocation();
  const onVisits = location.pathname === "/visits";
  const heading = breadcrumbs?.at(-1)?.name ?? title;
  return (
    <header
      className={cn(
        "shrink-0 border-b border-v3-border font-v3 text-v3-text",
        mode === "desktop" && "max-lg:hidden",
      )}
      data-testid="v3-top-bar"
    >
      <div className="flex h-14 items-center gap-2 px-2 sm:px-3 lg:h-[52px] lg:px-4">
        {!onVisits && <BackToVisits />}
        <h1 className="min-w-0 truncate text-[15px] font-semibold lg:text-[14px]">
          {heading}
        </h1>
        {pageHeader?.count !== undefined && (
          <span
            className="shrink-0 rounded-full px-2 text-[12px] tabular-nums text-v3-text-3 shadow-v3-hairline-strong"
            title={pageHeader.countLabel}
          >
            <span aria-hidden>{pageHeader.count}</span>
            <span className="sr-only">{pageHeader.countLabel}</span>
          </span>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <PendingRecordingsButton />
          {/* Background uploads: a v3.2 popover under the header icon (it
              no longer pushes the page down). */}
          <Popover open={uploadsOpen} onOpenChange={setUploadsOpen}>
            <PopoverAnchor asChild>
              <span className="inline-flex">
                <OpenBackgroundVisitUploadButton
                  isOpen={uploadsOpen}
                  onOpenChange={setUploadsOpen}
                />
              </span>
            </PopoverAnchor>
            <PopoverContent
              align="end"
              className="w-[min(380px,calc(100vw-24px))] p-0"
              collisionPadding={12}
              data-testid="uploads-popover"
              sideOffset={6}
            >
              <p className="px-4 pb-1 pt-3 text-[13px] font-semibold text-v3-text-3">
                {t("backgroundVisitUpload.title")}
              </p>
              <BackgroundVisitUploadPanel />
            </PopoverContent>
          </Popover>
          {pageHeader?.actions}
          <AccountMenu align="end" className="lg:hidden" />
        </div>
      </div>
    </header>
  );
};

const Frame = ({
  children,
  title,
  breadcrumbs,
  pageHeader,
  isLoading = false,
  topBar = "default",
}: Props): JSX.Element => {
  const { collapsed, setCollapsed } = useV3Shell();
  const { files, loading } = useVisitList();
  const location = useLocation();
  // "All visits" is current on the visits page, except while New visit
  // covers it (the sidebar then marks the New visit row).
  const newVisitOpen = useWorkspacePanelStore(
    (state) => state.active === "new-visit",
  );
  const onVisitsPage = location.pathname === "/visits" && !newVisitOpen;
  return (
    <div
      className="v3-shell v3-remap flex h-screen w-full bg-v3-canvas font-v3 text-v3-text supports-[height:100dvh]:h-dvh"
      data-sidebar={collapsed ? "hidden" : "shown"}
      data-testid="v3-shell"
    >
      {collapsed ? (
        <SidebarRail onExpand={() => setCollapsed(false)} />
      ) : (
        <aside className="hidden w-[272px] shrink-0 lg:block">
          <VisitBrowser
            loading={loading}
            onVisitsPage={onVisitsPage}
            visits={files}
            onCollapse={() => setCollapsed(true)}
          />
        </aside>
      )}
      <div className={cn("flex min-w-0 flex-1 flex-col lg:py-2 lg:pr-2")}>
        <div
          // New visit and Add recording open over this column on desktop
          // (WorkspacePanel).
          id={WORKSPACE_COLUMN_ID}
          className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-v3-panel lg:rounded-v3-panel lg:shadow-[0_0_0_1px_var(--v3-border),0_1px_2px_rgba(0,0,0,0.03)]"
        >
          {topBar !== "none" && (
            <TopBar
              breadcrumbs={breadcrumbs}
              mode={topBar}
              pageHeader={pageHeader}
              title={title}
            />
          )}
          {isLoading ? (
            <div className="flex w-full flex-1">
              <LoadingSpinner className="m-auto" size={8} />
            </div>
          ) : (
            <main className="relative flex-1 overflow-y-auto" id="app-content">
              {children}
            </main>
          )}
        </div>
      </div>
    </div>
  );
};

// The v3.2 app shell (VITE_APP_REDESIGN_ENABLED), as on iPad: the canvas,
// the visits sidebar on the left (hidden on demand) and the page in a raised
// panel. Under 1024px there is no sidebar: the visits page is the list and a
// visit has a back button.
export const V3Shell = (props: Props): JSX.Element => (
  <V3ShellProvider>
    <Frame {...props} />
  </V3ShellProvider>
);
